<template>
  <div class="flex flex-col">
    <Feature :feature-slug="featureNames.SSO">
      <label class="inline-flex items-center mb-4">
        <input
          v-model="user.isSso"
          type="checkbox"
          class="w-4 h-4 form-checkbox"
          :disabled="!!user?.id"
        >
        <span class="ml-2 text-base normal-case">{{ $t('modals.user.form.ssoLogin') }}</span>
      </label>
    </Feature>

    <div
      v-if="!user.isSso"
      class="w-full px-2 mb-6 -mx-2 form-group"
    >
      <input-text-validated
        ref="user.salutation"
        v-model="user.salutation"
        input-class="w-1/2"
        :label="$t('modals.user.form.salutation')"
        :placeholder="$t('modals.user.form.salutationPlaceholder')"
        :disabled="isDisabled('salutation')"
      />
    </div>
    <div
      v-if="!user.isSso"
      class="flex flex-wrap mb-6 -mx-2"
    >
      <div class="w-1/2 px-2 form-group">
        <input-text-validated
          ref="user.firstname"
          v-model="user.firstname"
          input-class="w-full"
          :label="$t('modals.user.form.firstname')"
          placeholder="Max"
          rules="required"
          :disabled="isDisabled('firstname')"
        />
      </div>

      <div class="w-1/2 px-2 form-group">
        <input-text-validated
          ref="user.lastname"
          v-model="user.lastname"
          input-class="w-full"
          :label="$t('modals.user.form.lastname')"
          :placeholder="$t('modals.user.form.lastnamePlaceholder')"
          rules="required"
          :disabled="isDisabled('lastname')"
        />
      </div>
    </div>

    <div class="flex flex-col w-full mb-6">
      <input-text-validated
        ref="user.email"
        v-model="user.email"
        input-class="w-full"
        :label="$t('modals.user.form.email')"
        placeholder="max.makler@max-makler.immobilien"
        rules="required|email"
        :disabled="isDisabled('email')"
      />
    </div>

    <div
      v-if="user.isSso"
      class="flex flex-col"
    >
      <div class="my-2">
        {{ $t('modals.user.form.ssoConnect') }}
      </div>
      <div
        v-for="ssoProvider of availableSsoProviders"
        :key="ssoProvider.issuer"
        class="flex flex-col w-1/2 gap-2"
      >
        <div class="w-1/2 px-2 form-group">
          <div class="flex flex-row items-center">
            <input
              v-model="user.ssoProviders[ssoProvider.issuer]"
              type="checkbox"
              class="w-4 h-4 form-checkbox"
            >
            <span class="ml-2 text-base normal-case">{{ ssoProvider.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="notificationSettings">
      <label class="block w-full">
        {{ $t('modals.user.form.notifications') }}
      </label>
      <div class="flex flex-row justify-between w-full mt-3 mb-3">
        <span class="ml-2 text-base normal-case">{{ $t('modals.user.form.newConversion') }}</span>

        <label class="inline-flex items-center">
          <input
            v-model="notificationSettings.newConversion.sms"
            type="checkbox"
            class="w-4 h-4 ml-4 border-2 border-gray-600 form-checkbox color-grey"
            disabled
          >
          <span class="ml-2 text-base normal-case">{{ $t('modals.user.form.sms') }}</span>
          <input
            v-model="notificationSettings.newConversion.email"
            type="checkbox"
            class="w-4 h-4 ml-4 border-2 border-gray-600 form-checkbox color-grey"
            disabled
          >
          <span class="ml-2 text-base normal-case">{{ $t('modals.user.form.emailChannel') }}</span>
        </label>
      </div>
      <div class="flex flex-row justify-between w-full mb-3">
        <span class="ml-2 text-base normal-case">{{ $t('modals.user.form.leadAssigned') }}</span>

        <label class="inline-flex items-center">
          <input
            v-model="notificationSettings.leadAssigned.sms"
            type="checkbox"
            class="w-4 h-4 ml-4 border-2 border-gray-600 form-checkbox color-grey"
            disabled
          >
          <span class="ml-2 text-base normal-case">{{ $t('modals.user.form.sms') }}</span>

          <input
            v-model="notificationSettings.leadAssigned.email"
            type="checkbox"
            class="w-4 h-4 ml-4 border-2 border-gray-600 form-checkbox color-grey"
            disabled
          >
          <span class="ml-2 text-base normal-case">{{ $t('modals.user.form.emailChannel') }}</span>
        </label>
      </div>
      <Feature
        v-slot="{feature}"
        :feature-slug="featureNames.NOTIFICATIONS"
      >
        <div
          v-if="feature.config.hasGeneralNotifications"
          class="flex flex-row justify-between w-full mb-3"
        >
          <span class="ml-2 text-base normal-case">{{ $t('modals.user.form.bottimmoNotifications') }}</span>

          <label class="inline-flex items-center">
            <input
              v-model="notificationSettings.news.email"
              type="checkbox"
              class="w-4 h-4 ml-4 border-2 border-gray-600 form-checkbox color-grey"
              disabled
            >
            <span class="ml-2 text-base normal-case">{{ $t('modals.user.form.emailChannel') }}</span>
          </label>
        </div>
      </Feature>
    </div>
    <div v-if="hasAuthorization && user.role">
      <div class="pt-6">
        <label class="block w-full">
          {{ $t('modals.user.form.permissionLevel') }}
        </label>
        <div class="role-select">
          <select
            v-model="user.role.id"
            class="w-1/2 form-select"
          >
            <option
              v-for="{id: roleId, name} in roles"
              :key="roleId"
              :value="roleId"
            >
              {{ name }}
            </option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GET_ROLES from '@/graphql/GetRoles.gql'

import InputTextValidated from '@/components/form/text-validated'
import feature from '@/mixins/feature'
import GET_COMPANY from '@/graphql/GetCompany.gql'
import eventBus, { eventNames } from '@/lib/eventBus'

export default {
  components: { InputTextValidated },
  mixins: [feature],
  props: {
    user: {
      type: Object,
      required: true
    },
    companyId: {
      type: String,
      required: true
    },
    disabled: {
      type: [Boolean, Array],
      default: false
    },
    settings: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      hasAuthorization: false,
      roles: []
    }
  },
  computed: {
    notificationSettings () {
      return this.settings?.notificationSettings
    },
    availableSsoProviders () {
      const { isActive, config } = this.$features.feature(this.featureNames.SSO) || {}
      return isActive && config?.providers
        ?.filter(({ issuer }) => this.user.ssoProviders?.[issuer])
        ?.map(({ name, issuer }) => ({ issuer, name }))
    }
  },
  apollo: {
    company: {
      query: GET_COMPANY,
      variables () {
        return {
          id: this.companyId
        }
      },
      fetchPolicy: 'cache-first'
    },
    roles: {
      query: GET_ROLES,
      update ({ roles }) {
        if (!Array.isArray(roles)) return []

        if (this.company.parentId) {
          const childCompanyRole = roles.find(role => {
            return role.slug === 'child-company-user'
          })

          if (childCompanyRole) {
            this.user.role = { id: childCompanyRole.id }
            return [childCompanyRole]
          } else {
            eventBus.$emit(eventNames.SHOW_SNACKBAR, { type: 'error', text: this.$t('modals.user.form.childCompanyRoleNotFound') })
            return []
          }
        }

        if (!this.user.role?.id) {
          this.user.role = { id: roles.find(role => role.isDefault)?.id }
        }
        return roles.filter(role => role.slug !== 'child-company-user')
      },
      fetchPolicy: 'cache-first'
    }
  },
  async created () {
    const companyFeatures = await this.$features.getFeaturesForCompany(this.companyId, {
      apolloProvider: this.$apollo.provider
    })
    this.hasAuthorization = companyFeatures[this.featureNames.AUTHORIZATION].isActive
  },
  methods: {
    isDisabled (field) {
      if (this.disabled) {
        return this.disabled === true ? true : this.disabled.includes(field)
      }

      return false
    }
  }
}
</script>

<style lang="postcss" scoped>
.color-grey {
  color: grey;
}
</style>
